import React from 'react';
import dayjs from 'dayjs';

import { Appointments_Order_By } from '@gql/base-types';
import { useGetMobileServiceAppointmentsQuery, GetMobileServiceAppointmentsQuery } from '../queries/getMobileServiceAppointments.query.generated';
import { useUser, useRooftop } from '@store';

import AppointmentsTabContent from './AppointmentsTabContent';
import { filterMobileServiceAppointments, toStatusFilter } from '../export/filterMobileServiceAppointments';

type Appointment = GetMobileServiceAppointmentsQuery['appointments'][number];

// LocalStorage keys for filter persistence — distinct from the consumer-pay
// keys so the two pages keep independent state.
const RANGE_KEY = 'mobile-service-appointments-range';
const START_KEY = 'mobile-service-appointments-start';
const END_KEY = 'mobile-service-appointments-end';

const getStartFor = (range: string): string => {
  switch (range) {
    case 'today': return dayjs().startOf('day').toISOString();
    case 'thisWeek': return dayjs().startOf('week').toISOString();
    case 'nextWeek': return dayjs().add(1, 'week').startOf('week').toISOString();
    case 'thisMonth': return dayjs().startOf('month').toISOString();
    case 'thisYear': return dayjs().startOf('year').toISOString();
    default: return dayjs().startOf('month').toISOString();
  }
};

const getEndFor = (range: string): string => {
  switch (range) {
    case 'today': return dayjs().endOf('day').toISOString();
    case 'thisWeek': return dayjs().endOf('week').toISOString();
    case 'nextWeek': return dayjs().add(1, 'week').endOf('week').toISOString();
    case 'thisMonth': return dayjs().endOf('month').toISOString();
    case 'thisYear': return dayjs().endOf('year').toISOString();
    default: return dayjs().endOf('month').toISOString();
  }
};

const getInitialRange = (): string => localStorage.getItem(RANGE_KEY) || 'thisMonth';

const getInitialStart = (range: string): string => {
  if (range === 'custom') {
    return localStorage.getItem(START_KEY) || getStartFor('thisMonth');
  }
  return getStartFor(range);
};

const getInitialEnd = (range: string): string => {
  if (range === 'custom') {
    return localStorage.getItem(END_KEY) || getEndFor('thisMonth');
  }
  return getEndFor(range);
};

/** The Appointments tab of the Mobile Service shell: the appointments query and its filters. */
const AppointmentsTab: React.FC = () => {
  const user = useUser();
  const rooftop = useRooftop();

  const customerId = rooftop?.id || user?.profile?.customer_id || 0;

  // Date range — user-selectable, persisted to localStorage
  const [range, setRange] = React.useState<string>(() => getInitialRange());
  const [start, setStart] = React.useState<string>(() => getInitialStart(getInitialRange()));
  const [end, setEnd] = React.useState<string>(() => getInitialEnd(getInitialRange()));

  // Search + status filters — client-side, not persisted across reloads
  const [search, setSearch] = React.useState<string>('');
  const [selectedStatuses, setSelectedStatuses] = React.useState<string[]>([]);

  const handleRangeChange = (value: string) => {
    localStorage.setItem(RANGE_KEY, value);
    setRange(value);
    if (value !== 'custom') {
      setStart(getStartFor(value));
      setEnd(getEndFor(value));
    }
  };

  const handleDateChange = (value: string, name: string) => {
    if (name === 'start') {
      const newDate = dayjs(value).startOf('day').toISOString();
      localStorage.setItem(START_KEY, newDate);
      setStart(newDate);
    } else {
      const newDate = dayjs(value).endOf('day').toISOString();
      localStorage.setItem(END_KEY, newDate);
      setEnd(newDate);
    }
  };

  // Pagination
  const [offset, setOffset] = React.useState(0);
  const [limit, setLimit] = React.useState(50);

  // Sorting
  const [orderBy, setOrderBy] = React.useState('appointment_time');
  const [order, setOrder] = React.useState<'asc' | 'desc'>('desc');

  const orderByGraphQL = React.useMemo<Appointments_Order_By[]>(() => {
    const arr: Appointments_Order_By[] = [];
    if (orderBy) {
      arr.push({ [orderBy]: `${order}_nulls_last` } as any);
    }
    arr.push({ id: 'desc_nulls_last' } as any);
    return arr;
  }, [orderBy, order]);

  // Fetch a wider window so client-side status / search filters still
  // yield a full page after filtering.
  const fetchLimit = Math.max(limit * 4, 200);

  // The status chips and the search box, as one object the list and the CSV export both read.
  const filters = React.useMemo(() => ({ search, selectedStatuses }), [search, selectedStatuses]);

  const { loading, error, data, refetch } = useGetMobileServiceAppointmentsQuery({
    variables: {
      customerId,
      start,
      end,
      limit: fetchLimit,
      offset: 0,
      orderBy: orderByGraphQL,
      typeFilter: { type: { _eq: 'mobile_service' } },
      // The aggregate (not the list) takes the chips, so it counts a status-filtered export exactly.
      statusFilter: toStatusFilter(selectedStatuses),
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    skip: !customerId,
  });

  // Reset pagination when any filter changes
  React.useEffect(() => { setOffset(0); }, [start, end, search, selectedStatuses]);

  // Client-side filter chain (type is filtered server-side via typeFilter): the status chips
  // and the search box, as one pure function shared with the CSV export's "all results" so the
  // file matches the list.
  const filtered: Appointment[] = React.useMemo(
    () => filterMobileServiceAppointments(data?.appointments ?? [], filters),
    [data, filters]
  );

  // Client-side pagination over the filtered set
  const paged = filtered.slice(offset, offset + limit);
  const totalCount = filtered.length;

  // How many rows "Export All Results" will write. The aggregate carries the window and the
  // status chips, so it is exact for those; the search box only exists on the client, and the
  // fetched rows are capped, so with a search active there is no honest number and the dialog
  // shows none.
  const allMatchingCount: number | null =
    search.trim().length > 0 ? null : data?.appointments_aggregate?.aggregate?.count ?? null;

  // Stable so the tab's header registration does not churn on every render.
  const handleRefetch = React.useCallback(async () => {
    await refetch();
  }, [refetch]);

  return (
    <AppointmentsTabContent
      loading={loading}
      error={error}
      refetch={handleRefetch}
      appointments={paged}
      allMatchingCount={allMatchingCount}
      totalCount={totalCount}
      filters={filters}
      customerId={customerId}
      orderByGraphQL={orderByGraphQL}
      offset={offset}
      limit={limit}
      setOffset={setOffset}
      setLimit={setLimit}
      orderBy={orderBy}
      order={order}
      setOrderBy={setOrderBy}
      setOrder={setOrder}
      range={range}
      start={start}
      end={end}
      onRangeChange={handleRangeChange}
      onDateChange={handleDateChange}
      search={search}
      setSearch={setSearch}
      selectedStatuses={selectedStatuses}
      setSelectedStatuses={setSelectedStatuses}
    />
  );
};

export default AppointmentsTab;
